/**
 * @copyright Copyright 2021-2024 Epic Systems Corporation
 * @file One row representing one app in the Live Apps widget
 * @module Epic.AppOrchard.Dashboard.LiveAppRow
 */

import { CheckIcon } from "@chakra-ui/icons";
import { Box, Image, List, ListItem } from "@chakra-ui/react";
import {
	AOLink,
	DownloadBuildIcon,
	DownloadBuildLink,
	ImplementationGuideLink,
	NewVersionLink,
} from "ao/components/Core";
import { useSiteInfoState } from "ao/state/siteInfo";
import { ILiveApp, IUserSecurity } from "ao/types";
import React, { FC, memo } from "react";
import { ImStatsBars } from "react-icons/im";
import { IoSpeedometerOutline } from "react-icons/io5";
import { VscFeedback } from "react-icons/vsc";
import { WidgetActionButton, WidgetAppBadge, WidgetAppRow, WidgetTag } from "../../../widgetHelpers";

interface IProps {
	app: ILiveApp;
	userSecurity: IUserSecurity;
	baseUrl: string;
	orgId: number;
	isGalleryEnabled: boolean | undefined;
}

/** One row representing one app in the Live Apps widget */
export const LiveAppRow: FC<IProps> = memo((props: IProps) => {
	const { app, userSecurity, baseUrl, orgId, isGalleryEnabled } = props;

	//#region state/handlers
	const { siteInfo } = useSiteInfoState((selectors) => selectors.getState(), []);

	const marketplaceUrl = `${siteInfo.showroomUrl}Listing?appId=`;
	const downloadUrl = `/Download?id=`;
	const statsUrl = `${baseUrl}Stats?OrgId=${orgId}&ClientId=${app.prodClientId}`;
	const metricsUrl = `${baseUrl}ApiMetrics?OrgId=${orgId}&ClientId=${app.prodClientId}`;
	const editReviewUrl = `${baseUrl}Review?id=${app.appId}`;
	const tesseractLogo = `${baseUrl}Content/images/Tesseract_Logo.png`;
	const viewReviewsUrl = `${baseUrl}Picked?id=${app.appId}&tab=reviews`;

	const isNewestAppVersion = app.appId === app.newestAppVersion;
	const hasTcPackages = app.turboChargerDocumentIds.length > 0;
	const hasBottomContent =
		hasTcPackages ||
		!isNewestAppVersion ||
		app.implementationGuideUrl ||
		app.hasApiMetricsForOrg ||
		app.hasApiStatsForOrg;

	let markedLiveByInfo = "";
	if (app.markedLiveAutomatically) {
		markedLiveByInfo = "automatically based on production API usage";
	} else if (app.markedLiveByUserName) {
		markedLiveByInfo = "by " + app.markedLiveByUserName;
	}
	const markedLiveInfo = app.markedLiveOn
		? `Marked live ${markedLiveByInfo} on ${app.markedLiveOn.toLocaleDateString()}`
		: null;
	const reviewsHelptext = `${app.numReviews} total reviews for this app. Click to see all reviews.`;

	const downloadPageUrl = (app.isAvailableInGallery ? marketplaceUrl : downloadUrl) + app.appId;
	const newestVersionDownloadPageUrl =
		(app.isNewestVersionAvailableInGallery ? marketplaceUrl : downloadUrl) + app.newestAppVersion;

	//#endregion

	return (
		<>
			<WidgetAppRow
				appId={app.appId}
				appName={app.appName}
				subtitle={<Box title="The organization that created this app">{app.appOrgName}</Box>}
				linkUrl={downloadPageUrl}
				linkTitle="Open this app's listing"
				belowLogoContent={
					<>
						{app.tesseractEnabled && (
							<Image
								src={tesseractLogo}
								h="18px"
								w="18px"
								title="This app is enabled for Tesseract."
								aria-label="This app is enabled for Tesseract"
								alt="Tesseract logo"
								display="inline-block"
							/>
						)}

						{hasTcPackages && <DownloadBuildIcon />}
					</>
				}
				additionalAppInfo={
					<List spacing="0.3em" display="inline-block">
						<ListItem>
							<WidgetTag
								label="Live"
								tooltipContent={markedLiveInfo}
								icon={CheckIcon}
							></WidgetTag>
						</ListItem>
					</List>
				}
				middleColumnContent={
					//disabled for now
					false &&
					app.numReviews > 0 &&
					isGalleryEnabled && (
						<AOLink url={viewReviewsUrl} target="_blank" title={reviewsHelptext}>
							<WidgetAppBadge title={reviewsHelptext} value={app.numReviews.toString()}>
								<VscFeedback size={20} color="darkblue" style={{ display: "inline" }} />
							</WidgetAppBadge>
						</AOLink>
					)
				}
				bottomContent={
					hasBottomContent && (
						<>
							{app.implementationGuideUrl && (
								<ImplementationGuideLink url={app.implementationGuideUrl} />
							)}
							{hasTcPackages && (
								<DownloadBuildLink appId={app.appId} showIcon></DownloadBuildLink>
							)}
							{!isNewestAppVersion && (
								<NewVersionLink
									appId={app.appId}
									newestAppId={app.newestAppVersion!}
									newVersionUrl={newestVersionDownloadPageUrl}
								/>
							)}
							{app.hasApiStatsForOrg && (
								<AOLink
									url={statsUrl}
									target="_blank"
									title="View stats for API calls this app has made"
								>
									<ImStatsBars
										color="darkgoldenrod"
										style={{ display: "inline", marginRight: "4px" }}
									/>
									API Stats
								</AOLink>
							)}
							{app.hasApiMetricsForOrg && (
								<AOLink
									url={metricsUrl}
									target="_blank"
									title="View performance metrics for API calls this app has made"
								>
									<IoSpeedometerOutline
										color="darkgreen"
										style={{ display: "inline", marginRight: "4px" }}
									/>
									API Performance
								</AOLink>
							)}
						</>
					)
				}
				actions={
					//disable for now
					false &&
					userSecurity.isEpicCustomer && (
						<WidgetActionButton
							as="a"
							href={editReviewUrl}
							target="_blank"
							colorScheme="blue"
							title={
								app.currentUserHasReview
									? "Edit your review for this app"
									: "Add a review for this app"
							}
							aria-label={
								app.currentUserHasReview
									? "Edit your review for this app"
									: "Add a review for this app"
							}
							leftIcon={<VscFeedback size={20} />}
						>
							{app.currentUserHasReview ? "Edit Review" : "Add Review"}
						</WidgetActionButton>
					)
				}
			></WidgetAppRow>
		</>
	);
});
